/**
 * Microcourse Studio — MicrocoursePlayer
 * 学生端只读播放器：slide 渲染 + segment 驱动自动播放 + TTS + 练习交互
 *
 * 功能：
 * - 幻灯片浏览（手动翻页 / 自动播放）
 * - segment 驱动逐步高亮（fragment）
 * - TTS 音频播放（后端 audioUrl 优先，降级浏览器语音）
 * - 练习块交互（quiz element）
 * - 学习进度上报
 * - 只读模式，无编辑能力
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react'
import type { MicrocourseProject } from '@ai-tutor/shared'
import { SlideCanvas } from './SlideCanvas'
import { PlaybackBar } from './playback/PlaybackBar'
import { useAutoplay } from './playback/useAutoplay'
import { injectThemeCSS } from './themes/themeRegistry'
import type { QuizSubmitResult } from './elements/ElementRenderer'
import { reportMicroQuiz } from '@/api/mistakes'
import { ChevronLeft, ChevronRight, Maximize2, Minimize2 } from 'lucide-react'

export interface MicrocoursePlayerProps {
  project: MicrocourseProject
  /** 初始 slide index */
  initialSlideIndex?: number
  /** 进度回调 (slideIndex, totalSlides) → 用于上报学习进度 */
  onProgressChange?: (slideIndex: number, totalSlides: number) => void
  /** 播放完毕回调 */
  onFinish?: () => void
  /** 是否显示全屏按钮 */
  showFullscreen?: boolean
  /** 关联课时 ID（传入后课堂练习错题自动同步到错题本） */
  lessonId?: string
  /** 学科（可选，缺省由后端从 lessons 表查询） */
  subject?: string
  /** 自定义 className */
  className?: string
}

export function MicrocoursePlayer({
  project,
  initialSlideIndex = 0,
  onProgressChange,
  onFinish,
  showFullscreen = true,
  lessonId,
  subject,
  className = '',
}: MicrocoursePlayerProps) {
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [quizTip, setQuizTip] = useState<string | null>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const quizTipTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const slides = useMemo(
    () => [...project.slides].sort((a, b) => a.order - b.order),
    [project.slides]
  )

  // Autoplay engine（fragment 级播放引擎，作为唯一位置来源）
  const autoplay = useAutoplay({
    slides,
    onProgress: onProgressChange,
    onFinish,
  })

  const activeSlideIndex = autoplay.currentSlideIndex
  const activeSlide = slides[activeSlideIndex] ?? slides[0]
  const activeFragmentIndex = autoplay.fragmentIndex

  // 当前 slide 的 segments 总数
  const totalSegments = activeSlide?.segments?.length ?? 0

  // 注入主题 CSS
  useEffect(() => {
    const cleanup = injectThemeCSS(project.theme)
    return cleanup
  }, [project.theme])

  // 注入自定义颜色 CSS 变量
  useEffect(() => {
    if (!project.customColors) return
    const { primary, accent, bg } = project.customColors
    const style = document.createElement('style')
    style.setAttribute('data-microcourse-custom-colors', '')
    style.textContent = `
      .reveal {
        --mc-primary: ${primary};
        --mc-accent: ${accent};
        --mc-bg: ${bg};
      }
    `
    document.head.appendChild(style)
    return () => style.remove()
  }, [project.customColors])

  // 注入转场动画 CSS（只注入一次）
  useEffect(() => {
    const style = document.createElement('style')
    style.setAttribute('data-microcourse-transitions', '')
    style.textContent = `
      @keyframes mc-slide-in { from { transform: translateX(3%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
      @keyframes mc-fade-in { from { opacity: 0; } to { opacity: 1; } }
      @keyframes mc-convex-in { from { transform: rotateY(15deg) translateX(3%); opacity: 0; } to { transform: rotateY(0) translateX(0); opacity: 1; } }
      @keyframes mc-concave-in { from { transform: rotateY(-15deg) translateX(-3%); opacity: 0; } to { transform: rotateY(0) translateX(0); opacity: 1; } }
      @keyframes mc-zoom-in { from { transform: scale(0.92); opacity: 0; } to { transform: scale(1); opacity: 1; } }
      .mc-transition { animation-duration: 0.5s; animation-fill-mode: both; }
      .mc-transition-slide { animation-name: mc-slide-in; }
      .mc-transition-fade { animation-name: mc-fade-in; }
      .mc-transition-convex { animation-name: mc-convex-in; }
      .mc-transition-concave { animation-name: mc-concave-in; }
      .mc-transition-zoom { animation-name: mc-zoom-in; }
      .mc-transition-none { animation: none !important; }
    `
    document.head.appendChild(style)
    return () => style.remove()
  }, [])

  // 注入演示排版 CSS（覆盖编辑模式的小字体，呈现幻灯片级排版）
  useEffect(() => {
    const style = document.createElement('style')
    style.setAttribute('data-microcourse-typography', '')
    style.textContent = `
      /* 标题：大字 + 强调色 + 底部分隔线 */
      .reveal h1, .reveal h2, .reveal h3, .reveal h4 {
        font-size: 1.45rem !important;
        line-height: 1.35 !important;
        color: var(--mc-primary, #1e40af) !important;
        border-bottom: 2px solid color-mix(in srgb, var(--mc-accent, #0984e3) 30%, transparent);
        padding-bottom: 0.4rem;
        margin-bottom: 1rem !important;
      }
      .reveal h1 > div, .reveal h2 > div, .reveal h3 > div, .reveal h4 > div {
        font-size: inherit !important;
        line-height: inherit !important;
      }
      /* 正文段落：演示级字号 */
      .reveal .space-y-1 > div {
        font-size: 1.02rem !important;
        line-height: 1.75 !important;
        margin-bottom: 0.8rem !important;
      }
      .reveal .space-y-1 > div .text-sm {
        font-size: inherit !important;
        line-height: inherit !important;
      }
      .reveal .space-y-1 > div p { margin-bottom: 0.5rem; }
      /* 列表 */
      .reveal ul, .reveal ol { padding-left: 1.4em; }
      .reveal li { margin-bottom: 0.3em; }
      .reveal li::marker { color: var(--mc-accent, #0984e3); }
      /* 高亮标记 */
      .reveal mark[data-type="key"] {
        background: linear-gradient(to bottom, transparent 60%, rgba(59,130,246,0.18) 60%);
        color: inherit; padding: 0 0.15em;
      }
      .reveal mark[data-type="result"] {
        background: linear-gradient(to bottom, transparent 60%, rgba(16,185,129,0.2) 60%);
        color: inherit; padding: 0 0.15em; font-weight: 600;
      }
      .reveal mark[data-type="pitfall"] {
        background: linear-gradient(to bottom, transparent 60%, rgba(245,158,11,0.2) 60%);
        color: inherit; padding: 0 0.15em;
      }
      /* 引用块：提示卡片风格 */
      .reveal blockquote {
        border-left: 4px solid var(--mc-accent, #0984e3) !important;
        background: color-mix(in srgb, var(--mc-accent, #0984e3) 6%, transparent) !important;
        border-radius: 0 10px 10px 0;
        padding: 0.7em 1.1em !important;
        font-style: normal !important;
        margin-top: 0.8rem;
        box-shadow: 0 1px 4px rgba(0,0,0,0.05);
      }
      /* 数学公式块 */
      .reveal .katex-display, .reveal .math-block {
        background: color-mix(in srgb, var(--mc-accent, #0984e3) 5%, transparent);
        border-left: 4px solid var(--mc-accent, #0984e3);
        border-radius: 6px;
        padding: 0.5em 1em;
        margin: 0.6em 0;
      }
      .reveal .katex { font-size: 1.08em; }
      /* 分隔线 */
      .reveal hr { border: none; border-top: 1px dashed #cbd5e1; margin: 0.8rem 0; }
    `
    document.head.appendChild(style)
    return () => style.remove()
  }, [])

  // 手动翻页：委托 fragment 级步进引擎
  const goToPrev = useCallback(() => {
    autoplay.stepPrev()
  }, [autoplay.stepPrev])

  const goToNext = useCallback(() => {
    autoplay.stepNext()
  }, [autoplay.stepNext])

  // 课堂练习答题上报：答错时同步到错题本
  const handleQuizResult = useCallback(
    (result: QuizSubmitResult) => {
      if (!lessonId) return
      const slideId = slides[autoplay.currentSlideIndex]?.id
      reportMicroQuiz({
        lessonId,
        elementId: result.elementId,
        slideId,
        lessonTitle: project.title,
        subject,
        quiz: result.quiz,
        userAnswer: result.userAnswer,
        isCorrect: result.isCorrect,
      })
        .then(() => {
          if (result.isCorrect) return
          setQuizTip('错题已同步到错题本')
          if (quizTipTimerRef.current) clearTimeout(quizTipTimerRef.current)
          quizTipTimerRef.current = setTimeout(() => setQuizTip(null), 3000)
        })
        .catch((e) => {
          console.error('课堂练习上报失败:', e)
        })
    },
    [lessonId, subject, project.title, slides, autoplay.currentSlideIndex]
  )

  // Unmount 时清理提示定时器
  useEffect(() => {
    return () => {
      if (quizTipTimerRef.current) clearTimeout(quizTipTimerRef.current)
    }
  }, [])

  // 键盘导航
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault()
        goToNext()
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault()
        goToPrev()
      } else if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [goToNext, goToPrev, isFullscreen])

  // 全屏切换
  const toggleFullscreen = useCallback(() => {
    if (!isFullscreen) {
      containerRef.current?.requestFullscreen?.()
      setIsFullscreen(true)
    } else {
      document.exitFullscreen?.()
      setIsFullscreen(false)
    }
  }, [isFullscreen])

  // 监听 fullscreen change
  useEffect(() => {
    const handler = () => {
      if (!document.fullscreenElement) {
        setIsFullscreen(false)
      }
    }
    document.addEventListener('fullscreenchange', handler)
    return () => document.removeEventListener('fullscreenchange', handler)
  }, [])

  if (!activeSlide) {
    return (
      <div className={`flex items-center justify-center h-64 text-slate-400 ${className}`}>
        暂无幻灯片内容
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className={`flex flex-col bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden ${
        isFullscreen ? 'fixed inset-0 z-50 rounded-none' : ''
      } ${className}`}
    >
      {/* Header: title + fullscreen */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100 bg-slate-50/50">
        <h3 className="text-sm font-medium text-slate-700 truncate">
          {project.title}
        </h3>
        {showFullscreen && (
          <button
            onClick={toggleFullscreen}
            className="p-1 rounded hover:bg-slate-200 transition-colors"
            title={isFullscreen ? '退出全屏' : '全屏'}
          >
            {isFullscreen ? (
              <Minimize2 className="w-4 h-4 text-slate-500" />
            ) : (
              <Maximize2 className="w-4 h-4 text-slate-500" />
            )}
          </button>
        )}
      </div>

      {/* Slide area */}
      <div className="relative flex-1 min-h-[400px]">
        {/* Prev/Next arrows */}
        <button
          onClick={goToPrev}
          disabled={!autoplay.canPrev}
          className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white/80 shadow border border-slate-200 hover:bg-white disabled:opacity-0 transition-all"
        >
          <ChevronLeft className="w-5 h-5 text-slate-600" />
        </button>
        <button
          onClick={goToNext}
          disabled={!autoplay.canNext}
          className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white/80 shadow border border-slate-200 hover:bg-white disabled:opacity-0 transition-all"
        >
          <ChevronRight className="w-5 h-5 text-slate-600" />
        </button>

        {/* Slide canvas */}
        <div
          key={`slide-${activeSlideIndex}-${project.transition}`}
          className={`reveal absolute inset-0 mc-transition mc-transition-${project.transition ?? 'slide'}`}
          style={{
            fontFamily: project.fontFamily,
            zoom: String(project.fontScale ?? 1),
          }}
        >
          <SlideCanvas
            slide={activeSlide}
            fragmentIndex={activeFragmentIndex}
            onQuizResult={handleQuizResult}
          />
        </div>

        {/* 错题同步提示 */}
        {quizTip && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 px-3 py-1.5 rounded-full bg-blue-600/90 text-white text-xs shadow-lg">
            ✓ {quizTip}
          </div>
        )}

        {/* Slide dots indicator */}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
          {slides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => autoplay.goToSlide(idx)}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === activeSlideIndex
                  ? 'bg-blue-500 w-4'
                  : 'bg-slate-300 hover:bg-slate-400'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Playback controls */}
      <PlaybackBar
        status={autoplay.status}
        currentSlideIndex={activeSlideIndex}
        totalSlides={slides.length}
        currentSegIndex={autoplay.currentSegIndex}
        totalSegments={totalSegments}
        statusText={autoplay.statusText}
        speaking={autoplay.speaking}
        onPlay={() => autoplay.start(activeSlideIndex)}
        onPause={autoplay.pause}
        onResume={autoplay.resume}
        onPrev={goToPrev}
        onNext={goToNext}
        prevDisabled={!autoplay.canPrev}
        nextDisabled={!autoplay.canNext}
      />
    </div>
  )
}
